import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import type { WeeklySlot } from '@/lib/publishQueues/types';
import { DEFAULT_WEEKLY_SLOT } from '@/lib/publishQueues/weeklySlots';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { WeeklySchedulePreview } from './WeeklySchedulePreview';
import { QueueTimezoneSelect } from './QueueTimezoneSelect';
import { QueueTimeSelect } from './QueueTimeSelect';
import { ScheduleSelect } from './ScheduleSelect';
import { QueueScheduleHelp } from './QueueScheduleHelp';

type Props = {
  timezone: string;
  onTimezoneChange: (timezone: string) => void;
  slots: WeeklySlot[];
  onSlotsChange: (slots: WeeklySlot[]) => void;
};

export function QueueWeeklySchedule({ timezone, onTimezoneChange, slots, onSlotsChange }: Props) {
  const { t, i18n } = useTranslation();
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, weekday) => {
    const date = new Date(Date.UTC(2026, 7, 2 + weekday, 12));
    return {
      weekday,
      label: new Intl.DateTimeFormat(i18n.language, { weekday: 'long', timeZone: 'UTC' }).format(date),
      shortLabel: new Intl.DateTimeFormat(i18n.language, { weekday: 'short', timeZone: 'UTC' }).format(date),
    };
  }), [i18n.language]);
  const removeSlot = (index: number) => onSlotsChange(slots.filter((_, i) => i !== index));
  const updateSlot = (index: number, patch: Partial<WeeklySlot>) => {
    onSlotsChange(slots.map((slot, i) => i === index ? { ...slot, ...patch } : slot));
  };

  return (
    <fieldset aria-labelledby="queue-weekly-schedule-label" className="min-w-0 space-y-4">
      <legend className="text-sm font-medium">
        <span className="inline-flex items-center gap-1">
          <span id="queue-weekly-schedule-label">{t('publishQueues.form.slots')}</span>
          <QueueScheduleHelp />
        </span>
      </legend>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0 space-y-2 sm:max-w-sm sm:flex-1">
          <Label htmlFor="queue-timezone">{t('publishQueues.form.timezone')}</Label>
          <QueueTimezoneSelect value={timezone} onChange={onTimezoneChange} />
        </div>
        <Button type="button" variant="outline" size="sm" className="self-end"
          onClick={() => onSlotsChange([...slots, { ...DEFAULT_WEEKLY_SLOT }])}>
          <Plus className="mr-2 h-4 w-4" />{t('publishQueues.form.addSlot')}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">{t('publishQueues.form.rangeHint')}</p>
      <WeeklySchedulePreview slots={slots} weekdays={weekdays}
        onAddSlot={(slot) => onSlotsChange([...slots, slot])}
        onUpdateSlot={updateSlot}
        onRemoveSlot={slots.length > 1 ? removeSlot : undefined}
        onSelectSlot={(index, edge = 'start') => document.getElementById(`queue-${edge}-${index}`)?.focus()} />
      <div className="space-y-2">
        {slots.map((slot, index) => (
          <div key={index} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-3 rounded-lg border bg-card p-3 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
            <div className="col-span-2 min-w-0 space-y-1.5 sm:col-span-1">
              <Label htmlFor={`queue-weekday-${index}`} className="text-xs text-muted-foreground">{t('publishQueues.form.weekday')}</Label>
              <ScheduleSelect id={`queue-weekday-${index}`} value={String(slot.weekday)}
                options={weekdays.map((day) => ({ value: String(day.weekday), label: day.label }))}
                searchLabel={t('publishQueues.form.searchWeekday')}
                onChange={(value) => updateSlot(index, { weekday: Number(value) })} />
            </div>
            <div className="col-start-1 row-start-2 min-w-0 space-y-1.5 sm:col-start-2 sm:row-start-1">
              <Label htmlFor={`queue-start-${index}`} className="text-xs text-muted-foreground">{t('publishQueues.form.startTime')}</Label>
              <QueueTimeSelect id={`queue-start-${index}`} value={slot.timeLocal}
                onChange={(value) => updateSlot(index, { timeLocal: value })} />
            </div>
            <div className="col-start-2 row-start-2 min-w-0 space-y-1.5 sm:col-start-3 sm:row-start-1">
              <Label htmlFor={`queue-end-${index}`} className="text-xs text-muted-foreground">{t('publishQueues.form.endTime')}</Label>
              <QueueTimeSelect id={`queue-end-${index}`} value={slot.endTimeLocal ?? slot.timeLocal}
                onChange={(value) => updateSlot(index, { endTimeLocal: value })} />
            </div>
            <Button type="button" variant="ghost" size="icon" className="col-start-3 row-start-1 sm:col-start-4"
              disabled={slots.length === 1} aria-label={t('publishQueues.form.removeSlot')}
              onClick={() => removeSlot(index)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t('publishQueues.form.exactTimeHint')}</p>
    </fieldset>
  );
}
